import React, { useState } from 'react';
import { loader } from 'graphql.macro';
import { useQuery, useMutation, Reference, ApolloError } from '@apollo/client';
import { Table, Button, Popconfirm, message } from 'antd';
import { PlusOutlined, WarningFilled, SearchOutlined, FilterFilled } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { logger, addressFormatFunction } from '../../../utils/helpers';
import {
  GetAllTransporterDriversQuery,
  GetAllTransporterDriversQueryVariables,
  DeleteTransporterDriverMutation,
  DeleteTransporterDriverMutationVariables,
  TransporterDrivers,
} from '../../../graphql/graphql-types';
import { AddressType } from '../../../utils/types';
import ColumnSearchComponent from '../../../components/ColumnSearchComponent';
import colors from '../../../scss/variables.module.scss';

/* View All Transporter Driver screen type */
type ViewAllTransporterDriversScreenType = Pick<
  TransporterDrivers,
  'drivingLicence' | 'mobile' | 'name' | 'profileCompleted' | 'id'
> & {
  /* address of the driver */
  address?: AddressType;
};

/* loading get all transporter driver's query with the help of loader */
const getAllTransporterDriversQuery = loader(
  '../../../graphql/queries/getAllTransporterDriversQuery.graphql',
);

/* loading delete transporter driver mutation with the help of loader */
const deleteTransporterDriverMutation = loader(
  '../../../graphql/mutations/deleteTransporterDriverMutation.graphql',
);

/* React functional component */
const ViewAllTransporterDriversScreen = () => {
  /* useState to store selected transporter driver id when user hits delete button which will then use for loading indication */
  const [deleteTransporterDriverId, setDeleteTransporterDriverId] = useState<number | null>(null);

  /* extracting navigate from useNavigate hook */
  const navigate = useNavigate();

  /* get all transporter driver query */
  const { data, error, loading } = useQuery<
    GetAllTransporterDriversQuery,
    GetAllTransporterDriversQueryVariables
  >(getAllTransporterDriversQuery);

  /* delete transporter driver mutation */
  const [deleteTransporterDriver] = useMutation<
    DeleteTransporterDriverMutation,
    DeleteTransporterDriverMutationVariables
  >(deleteTransporterDriverMutation);

  if (error) {
    return <div className="errorText">{error.message}</div>;
  }

  /* function to handle delete transporter driver */
  const handleDeleteTransporterDriver = (driverId: number) => {
    setDeleteTransporterDriverId(driverId);
    deleteTransporterDriver({
      variables: {
        id: driverId,
      },
      update(cache, { data: deleteData }) {
        /* to get the id of the data to be deleted */
        const idToRemove = deleteData?.deleteTransporterDriver?.id;
        cache.modify({
          fields: {
            transporterDrivers(existingTransporterDrivers: Array<Reference>, { readField }) {
              if (idToRemove) {
                return existingTransporterDrivers.filter(
                  (transporterDriversData) =>
                    idToRemove !== readField('id', transporterDriversData),
                );
              }
              return existingTransporterDrivers;
            },
          },
        });
      },
    })
      .then(() => {
        setDeleteTransporterDriverId(null);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        message.success('Driver has been successfully deleted.');
      })
      .catch((deleteTransporterDriverError: ApolloError) => {
        let errorMsg = deleteTransporterDriverError.message;
        setDeleteTransporterDriverId(null);
        if (deleteTransporterDriverError.message.includes('Foreign key violation.')) {
          errorMsg = 'Cannot delete since driver is linked with shipment(s).';
        }
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        message.error(errorMsg);
        logger(deleteTransporterDriverError);
      });
  };

  return (
    <>
      <Button
        type="primary"
        icon={<PlusOutlined style={{ fontSize: 15 }} />}
        className="createAndNavigateButton"
        onClick={() => {
          navigate('create');
        }}
      >
        Create driver
      </Button>
      <Table<ViewAllTransporterDriversScreenType>
        dataSource={data ? data.getAllTransporterDrivers : []}
        loading={loading}
        className="tableStyle"
        bordered
        size="small"
        pagination={{ showSizeChanger: true }}
      >
        <Table.Column<ViewAllTransporterDriversScreenType>
          key="name"
          title="Driver Name"
          dataIndex="name"
          align="center"
          render={(driversName) => {
            return <span style={{ textTransform: 'capitalize' }}>{driversName || '-'}</span>;
          }}
          onFilter={(value, record) => {
            return record.name
              ? record.name.toLowerCase().includes(value.toString().toLowerCase())
              : false;
          }}
          filterDropdown={(filterDropDownProp) => (
            <ColumnSearchComponent filterBy="driver name" filterDropDownProp={filterDropDownProp} />
          )}
          filterIcon={(filtered) => {
            return <SearchOutlined className={filtered ? 'searchIcon' : ''} />;
          }}
        />
        <Table.Column<ViewAllTransporterDriversScreenType>
          key="address"
          title="Address"
          dataIndex="address"
          width="30%"
          align="center"
          render={(text, record) => {
            if (record.address) {
              return (
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {addressFormatFunction(record.address, 'doubleLine')}
                </div>
              );
            }
          }}
          onFilter={(value, record) => {
            if (record.address) {
              const address = record.address;
              const addressData = `${address.address} ${address.city} ${
                address.state ? address.state : ''
              } ${address.pincode}`;
              return addressData
                ? addressData.toString().toLowerCase().includes(value.toString().toLowerCase())
                : false;
            }
            return false;
          }}
          filterDropdown={(filterDropDownProp) => (
            <ColumnSearchComponent filterBy="address" filterDropDownProp={filterDropDownProp} />
          )}
          filterIcon={(filtered) => {
            return <SearchOutlined className={filtered ? 'searchIcon' : ''} />;
          }}
        />
        <Table.Column<ViewAllTransporterDriversScreenType>
          key="mobile"
          title="Mobile"
          dataIndex="mobile"
          align="center"
          render={(mobileNumber: string) => mobileNumber || '-'}
          onFilter={(value, record) => {
            return record.mobile
              ? record.mobile.toLowerCase().includes(value.toString().toLowerCase())
              : false;
          }}
          filterDropdown={(filterDropDownProp) => (
            <ColumnSearchComponent
              filterBy="mobile number"
              filterDropDownProp={filterDropDownProp}
            />
          )}
          filterIcon={(filtered) => {
            return <SearchOutlined className={filtered ? 'searchIcon' : ''} />;
          }}
        />
        <Table.Column<ViewAllTransporterDriversScreenType>
          key="drivingLicence"
          title="Driving Licence  No"
          dataIndex="drivingLicence"
          align="center"
          render={(drivingLicence: string) => drivingLicence || '-'}
          onFilter={(value, record) => {
            return record.drivingLicence
              ? record.drivingLicence.toLowerCase().includes(value.toString().toLowerCase())
              : false;
          }}
          filterDropdown={(filterDropDownProp) => (
            <ColumnSearchComponent
              filterBy="driving licence number"
              filterDropDownProp={filterDropDownProp}
            />
          )}
          filterIcon={(filtered) => {
            return <SearchOutlined className={filtered ? 'searchIcon' : ''} />;
          }}
        />
        <Table.Column<ViewAllTransporterDriversScreenType>
          key="profileCompleted"
          title="Profile Status"
          dataIndex="profileCompleted"
          align="center"
          render={(profileCompleted: boolean) => {
            return profileCompleted ? 'Complete' : 'Incomplete';
          }}
          onFilter={(value, record) => record.profileCompleted === value}
          filterIcon={(filtered) => {
            return <FilterFilled className={filtered ? 'searchIcon' : ''} />;
          }}
          filters={[
            {
              text: 'Complete',
              value: true,
            },
            {
              text: 'Incomplete',
              value: false,
            },
          ]}
        />
        <Table.Column<ViewAllTransporterDriversScreenType>
          key="actions"
          title="Actions"
          dataIndex="actions"
          align="center"
          render={(text, record) => {
            return (
              <div className="buttonContainer">
                <Button
                  onClick={() => {
                    navigate(`${record.id}/edit`);
                  }}
                >
                  Edit
                </Button>
                <Popconfirm
                  title="Delete Driver. Are you sure?"
                  okText="Yes"
                  onConfirm={() => {
                    handleDeleteTransporterDriver(record.id);
                  }}
                  okButtonProps={{ style: { borderRadius: 4, marginLeft: 2 } }}
                  cancelButtonProps={{ style: { borderRadius: 4, marginRight: 7 } }}
                  cancelText="No"
                  icon={<WarningFilled style={{ color: colors.deleteUserIconColor }} />}
                >
                  <Button
                    className="deleteButton"
                    loading={record.id === deleteTransporterDriverId}
                  >
                    Delete
                  </Button>
                </Popconfirm>
              </div>
            );
          }}
        />
      </Table>
    </>
  );
};
export default ViewAllTransporterDriversScreen;
